import { useCallback, useEffect, useMemo, useReducer, useRef, type ReactNode } from "react";
import { CombinedGraphQLErrors } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { VIDEO_ASSETS_BY_IDS_MAX } from "@athena/common/utils/videoDetails";
import {
    CompleteVideoUploadDocument,
    CreateVideoUploadDocument,
    DeleteVideoAssetDocument,
    PublishVideoAssetDocument,
    VideoAssetsByIdsDocument,
} from "@/graphql/media.generated";
import { runVideoUpload } from "@/lib/videoUpload";
import {
    EMPTY_QUEUE,
    isTransferred,
    isTransferring,
    missingDetails,
    partitionFiles,
    assetIdsToPoll,
    queueReducer,
    summarize,
    type QueueItem,
    type QueueState,
} from "./uploadQueue";
import {
    MAX_CONCURRENT_UPLOADS,
    POLL_INTERVAL_MS,
    UploadQueueContext,
    type UploadQueueContextValue,
} from "./uploadQueueContext";

const newKey = () => crypto.randomUUID();

/**
 * Holds the bulk uploader's queue above the router.
 *
 * Above it because uploads must outlive the page that started them: an operator who drops forty
 * videos and then goes to check a course should not come back to forty canceled transfers. Leaving
 * the *app* still loses them — the bytes live in this tab — so the unload warning below says so first.
 */
export function UploadQueueProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const client = useApolloClient();
    const [
        state,
        dispatch,
    ] = useReducer(queueReducer, EMPTY_QUEUE);

    /** The latest state, for async work that outlives the render that started it. */
    const stateRef = useRef<QueueState>(state);
    stateRef.current = state;
    /** One per transfer in flight; `remove` aborts through it. */
    const controllers = useRef<Map<string, AbortController>>(new Map());

    const runUpload = useCallback(
        async (item: QueueItem) => {
            const file = item.file;
            if (!file) return;
            const controller = new AbortController();
            controllers.current.set(item.key, controller);
            dispatch({ type: "started", key: item.key });
            try {
                await runVideoUpload(
                    file,
                    {
                        create: async () => {
                            const { data } = await client.mutate({
                                mutation: CreateVideoUploadDocument,
                                variables: {
                                    input: {
                                        organizationId: item.organizationId,
                                        title: item.details.title.trim() || file.name,
                                        filename: file.name,
                                        contentType: file.type,
                                        fileSizeBytes: file.size,
                                        draft: true,
                                    },
                                },
                            });
                            if (!data?.createVideoUpload) throw new Error("Upload could not be started");
                            return data.createVideoUpload;
                        },
                        complete: (input) =>
                            client.mutate({ mutation: CompleteVideoUploadDocument, variables: { input } }),
                        discard: (id) => client.mutate({ mutation: DeleteVideoAssetDocument, variables: { id } }),
                    },
                    {
                        signal: controller.signal,
                        onCreated: (assetId) => dispatch({ type: "created", key: item.key, assetId }),
                        onProgress: (progress) => dispatch({ type: "progressed", key: item.key, progress }),
                    },
                );
                dispatch({ type: "transferred", key: item.key });
            } catch (e) {
                // Canceled means removed: the row is already gone and there is nothing to report.
                if (controller.signal.aborted) return;
                const message = (e as Error).message;
                Sentry.captureException(e);
                dispatch({ type: "uploadFailed", key: item.key, message });
                toast.error(t("admin.bulkUpload.uploadFailedToast", { name: file.name, message }));
            } finally {
                controllers.current.delete(item.key);
            }
        },
        [
            client,
            t,
        ],
    );

    // Fill free upload slots, oldest first.
    useEffect(() => {
        const active = state.items.filter((i) => i.phase === "uploading").length;
        const waiting = state.items.filter((i) => i.phase === "queued" && i.file);
        for (const item of waiting.slice(0, Math.max(0, MAX_CONCURRENT_UPLOADS - active))) void runUpload(item);
    }, [
        state.items,
        runUpload,
    ]);

    const publish = useCallback(
        async (key: string) => {
            const item = stateRef.current.items.find((i) => i.key === key);
            if (!item?.assetId || item.publishing || item.published) return;
            const missing = missingDetails(item);
            if (missing.length > 0) {
                dispatch({
                    type: "publishFailed",
                    key,
                    message: t("admin.bulkUpload.missing", {
                        fields: missing.map((f) => t(`admin.bulkUpload.field_${f}`)).join(", "),
                    }),
                });
                return;
            }
            dispatch({ type: "publishStarted", key });
            try {
                await client.mutate({
                    mutation: PublishVideoAssetDocument,
                    variables: {
                        id: item.assetId,
                        input: {
                            title: item.details.title.trim(),
                            description: item.details.description.trim(),
                            presenterId: item.details.presenterId ?? "",
                            tagIds: [...item.details.tagIds],
                            restricted: item.details.restricted,
                            brands: [...item.details.brands],
                        },
                    },
                });
                dispatch({ type: "published", key });
                // The Library and the pickers list finished videos only, and this one just became one.
                client.cache.evict({ fieldName: "videoAssets" });
                client.cache.evict({ fieldName: "videoAssetsCount" });
                client.cache.gc();
            } catch (e) {
                const message = (e as Error).message;
                Sentry.captureException(e);
                dispatch({ type: "publishFailed", key, message });
                toast.error(t("admin.bulkUpload.publishFailedToast", { name: item.details.title, message }));
            }
        },
        [
            client,
            t,
        ],
    );

    // Publish anything whose Finish was pressed mid-transfer, the moment its bytes are in.
    useEffect(() => {
        for (const item of state.items) {
            if (item.finishWhenUploaded && isTransferred(item)) void publish(item.key);
        }
    }, [
        state.items,
        publish,
    ]);

    // Poll processing status for the whole queue, a chunk per request.
    const pollIds = assetIdsToPoll(state).join(",");
    useEffect(() => {
        if (!pollIds) return;
        const ids = pollIds.split(",");
        const tick = async () => {
            for (let i = 0; i < ids.length; i += VIDEO_ASSETS_BY_IDS_MAX) {
                const chunk = ids.slice(i, i + VIDEO_ASSETS_BY_IDS_MAX);
                try {
                    const { data } = await client.query({
                        query: VideoAssetsByIdsDocument,
                        variables: { ids: chunk },
                        fetchPolicy: "network-only",
                    });
                    if (data) dispatch({ type: "polled", requestedIds: chunk, assets: data.videoAssetsByIds });
                } catch {
                    // A missed poll is retried on the next tick; nothing to tell the operator yet.
                }
            }
        };
        const timer = window.setInterval(() => void tick(), POLL_INTERVAL_MS);
        return () => window.clearInterval(timer);
    }, [
        pollIds,
        client,
    ]);

    // Closing the tab mid-transfer loses the bytes, and unsaved details are lost either way.
    const atRisk = state.items.some((i) => isTransferring(i) || (i.detailsDirty && !i.published));
    useEffect(() => {
        if (!atRisk) return;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Chrome still requires this for the prompt to appear; the text itself is ignored.
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [atRisk]);

    const addFiles = useCallback((files: readonly File[], organizationId: string | undefined) => {
        const { accepted, rejected } = partitionFiles(files, stateRef.current.items);
        if (accepted.length > 0) {
            dispatch({
                type: "added",
                organizationId,
                items: accepted.map((file) => ({ key: newKey(), file })),
            });
        }
        return rejected;
    }, []);

    const remove = useCallback(
        async (key: string) => {
            const item = stateRef.current.items.find((i) => i.key === key);
            if (!item) return;
            const controller = controllers.current.get(key);
            if (controller) {
                // The runner deletes the half-made asset on abort.
                controller.abort();
            } else if (item.assetId && !item.published) {
                try {
                    await client.mutate({ mutation: DeleteVideoAssetDocument, variables: { id: item.assetId } });
                } catch (e) {
                    /*
                     * Not found means it is already gone — discarded from another tab, or by an
                     * earlier session of this queue — which is what the operator asked for. Clearing
                     * the row is the right answer; reporting a failure would leave a row nothing can
                     * ever remove.
                     */
                    const alreadyGone =
                        CombinedGraphQLErrors.is(e) && e.errors.some((err) => err.extensions?.code === "NOT_FOUND");
                    if (alreadyGone) {
                        dispatch({ type: "removed", key });
                        return;
                    }
                    toast.error(
                        t("admin.bulkUpload.discardFailedToast", {
                            name: item.filename,
                            message: (e as Error).message,
                        }),
                    );
                    return;
                }
            }
            dispatch({ type: "removed", key });
        },
        [
            client,
            t,
        ],
    );

    const value = useMemo<UploadQueueContextValue>(
        () => ({
            state,
            summary: summarize(state),
            addFiles,
            recover: (drafts) => dispatch({ type: "recovered", drafts, keyFor: () => newKey() }),
            retry: (key) => dispatch({ type: "retried", key }),
            remove,
            editDetails: (key, patch) => dispatch({ type: "detailsEdited", key, patch }),
            copyDetails: (fromKey, fields) => dispatch({ type: "detailsCopied", fromKey, fields }),
            finish: (key) => {
                const item = stateRef.current.items.find((i) => i.key === key);
                if (!item) return;
                if (isTransferred(item)) void publish(key);
                else dispatch({ type: "finishRequested", key });
            },
            clearFinished: () => dispatch({ type: "clearedFinished" }),
        }),
        [
            state,
            addFiles,
            remove,
            publish,
        ],
    );

    return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}
